import { Routes, Route } from 'react-router-dom'
import { Suspense, lazy, useEffect } from 'react'
import Layout from './components/Layout/Layout'
import { SidebarNavProvider } from './context/SidebarNavContext'
import { FavoritesProvider } from './context/FavoritesContext'
import { Loader } from './components/UI/LoadingSpinner'
import ErrorBoundary from './components/ErrorBoundary'
import RouteScrollReset from './components/Layout/RouteScrollReset'
import ProtectedRoute, { PublicOnlyRoute } from './components/Auth/ProtectedRoute'
import {
  TalentsPageGuard,
  CandidatesPageGuard,
  EmployeesPageGuard,
  EmployeesAllPageGuard,
  PreSalesPageGuard,
  TraineesPageGuard,
  StatusManagementPageGuard,
  StageManagementPageGuard,
  TalentFormPageGuard,
  TalentEditPageGuard,
  TalentDetailPageGuard,
  CalendarPageGuard,
  BackupManagementPageGuard,
  ActivityFeedPageGuard
} from './components/Auth/PageGuards'

// All internal pages are eagerly imported — no Suspense inside the app
import Dashboard from './pages/Dashboard'
import Talents from './pages/Talents'
import TalentDetail from './pages/TalentDetail'
import TalentForm from './pages/TalentForm'
import TalentEdit from './pages/TalentEdit'
import Candidates from './pages/Candidates'
import CandidatesByStage from './pages/CandidatesByStage'
import RejectedCandidates from './pages/RejectedCandidates'
import Employees from './pages/Employees'
import EmployeesAll from './pages/EmployeesAll'
import EmployeesActive from './pages/EmployeesActive'
import EmployeesProject from './pages/EmployeesProject'
import PreSales from './pages/PreSales'
import PresaleHistory from './pages/PresaleHistory'
import Trainees from './pages/Trainees'
import MyProfile from './pages/MyProfile'
import Favorites from './pages/Favorites'
import Login from './pages/Login'
import OAuthCallback from './pages/OAuthCallback'
import Unauthorized from './pages/Unauthorized'
import NotFound from './pages/NotFound'
import Wiki from './pages/Wiki'
import Calendar from './pages/Calendar'
import BackupManagement from './pages/BackupManagement'
import Statistics from './pages/Statistics'
import StatusManagement from './pages/StatusManagement'
import StageManagement from './pages/StageManagement'
import OrderManagement from './pages/OrderManagement'
import JobApplications from './pages/JobApplications'
import JobApplicationDetail from './pages/JobApplicationDetail'
import JobApplicationForm from './pages/JobApplicationForm'
import TalentTemplates from './pages/TalentTemplates'
import TalentTemplateDetail from './pages/TalentTemplateDetail'
import TalentPosts from './pages/TalentPosts'
import TalentPostDetail from './pages/TalentPostDetail'
import PromptTemplates from './pages/PromptTemplates'
import PromptTemplateDetail from './pages/PromptTemplateDetail'
import NotificationSettings from './pages/NotificationSettings'
import PlaybookSettings from './pages/PlaybookSettings'
import StagesDashboard from './pages/StagesDashboard'
import ActivityFeed from './pages/ActivityFeed'
import { NotificationsPageWidget } from './components/Layout/NotificationsWidget'
import SupportTickets from './pages/SupportTickets'

// Lazy imports — public pages outside Layout (have their own Suspense)
const Catalogue = lazy(() => import('./pages/Catalogue'))
const TalentPublicProfile = lazy(() => import('./pages/TalentPublicProfile'))
const PostsFeed = lazy(() => import('./pages/PostsFeed'))
const PostDetail = lazy(() => import('./pages/PostDetail'))
const ProfessionPricing = lazy(() => import('./pages/ProfessionPricing'))

function App() {
  // Инициализация темы при загрузке приложения
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    
    if (savedTheme === 'dark' || (!savedTheme && systemPrefersDark)) {
      document.documentElement.classList.add('dark');
      document.documentElement.dataset.theme = 'dark';
    } else {
      document.documentElement.classList.remove('dark');
      document.documentElement.dataset.theme = 'light';
    }
  }, []);

  return (
    <>
      <RouteScrollReset />
      <ErrorBoundary fullPage>
        <Routes>
        {/* Public routes without layout */}
        <Route path="/catalogue" element={
          <Suspense fallback={<Loader variant="page" size="lg" />}>
            <Catalogue />
          </Suspense>
        } />
        <Route path="/profile/:slug" element={
          <Suspense fallback={<Loader variant="page" size="lg" />}>
            <TalentPublicProfile />
          </Suspense>
        } />
        <Route path="/posts" element={
          <Suspense fallback={<Loader variant="page" size="lg" />}>
            <PostsFeed />
          </Suspense>
        } />
        <Route path="/pricing" element={
          <Suspense fallback={<Loader variant="page" size="lg" />}>
            <ProfessionPricing />
          </Suspense>
        } />
        <Route path="/posts/:slug" element={
          <Suspense fallback={<Loader variant="page" size="lg" />}>
            <PostDetail />
          </Suspense>
        } />

        {/* Routes with main layout */}
        <Route path="/*" element={
          <SidebarNavProvider>
            <FavoritesProvider>
            <Layout>
              <Routes>
                <Route path="/login" element={
                  <PublicOnlyRoute>
                    <Login />
                  </PublicOnlyRoute>
                } />

                <Route path="/oauth-callback" element={<OAuthCallback />} />
                <Route path="/oauth-callback/login" element={<OAuthCallback />} />
                <Route path="/unauthorized" element={<Unauthorized />} />

                {/* Protected routes - require authentication */}
                <Route element={<ProtectedRoute />}>
                  <Route path="/" element={<Dashboard />} />
                  <Route path="/wiki" element={<Wiki />} />

                  {/* My Profile - доступен всем авторизированным пользователям */}
                  <Route path="/my-profile" element={<MyProfile />} />

                  {/* Favorites — своё избранное доступно любому авторизованному,
                      чужое гейтится внутри страницы по talent_favorite.read-all */}
                  <Route path="/favorites" element={<Favorites />} />

                  {/* Calendar - доступен пользователям с правом talent.list */}
                  <Route path="/calendar" element={
                    <CalendarPageGuard>
                      <Calendar />
                    </CalendarPageGuard>
                  } />

                  {/* Backup Management - доступен пользователям с правом backup.read */}
                  <Route path="/backup" element={
                    <BackupManagementPageGuard>
                      <BackupManagement />
                    </BackupManagementPageGuard>
                  } />

                  {/* Activity Feed */}
                  <Route path="/activity-feed" element={
                    <ActivityFeedPageGuard>
                      <ActivityFeed />
                    </ActivityFeedPageGuard>
                  } />

                  {/* Notifications full page */}
                  <Route path="/notifications" element={<NotificationsPageWidget />} />

                  {/* Support tickets (for this service) */}
                  <Route path="/support/tickets" element={<SupportTickets />} />

                  {/* Statistics - доступен авторизированным пользователям */}
                  <Route path="/statistics" element={<Statistics />} />
                  <Route path="/statistics/dashboard" element={<Statistics />} />
                  <Route path="/statistics/managers-talents" element={<Statistics />} />
                  <Route path="/statistics/managers-videos" element={<Statistics />} />
                  <Route path="/statistics/managers-youtube" element={<Statistics />} />
                  <Route path="/statistics/managers-hired" element={<Statistics />} />
                  <Route path="/statistics/managers-started" element={<Statistics />} />
                  <Route path="/statistics/custom-reports" element={<Statistics />} />
                  <Route path="/statistics/period-comparison" element={<Statistics />} />
                  <Route path="/statistics/period-comparison-videos" element={<Statistics />} />
                  <Route path="/statistics/period-comparison-youtube" element={<Statistics />} />
                  <Route path="/statistics/managers-portrait" element={<Statistics />} />
                  <Route path="/statistics/period-comparison-portrait" element={<Statistics />} />
                  <Route path="/statistics/period-comparison-hired" element={<Statistics />} />
                  <Route path="/statistics/period-comparison-started" element={<Statistics />} />
                  <Route path="/statistics/onboarding" element={<Statistics />} />
                  <Route path="/statistics/hr-recruitment" element={<Statistics />} />

                  {/* Talents routes with permission checks */}
                  <Route path="/talents" element={
                    <TalentsPageGuard>
                      <Talents />
                    </TalentsPageGuard>
                  } />
                  <Route path="/talents/new" element={
                    <TalentFormPageGuard>
                      <TalentForm />
                    </TalentFormPageGuard>
                  } />
                  <Route path="/talents/:id" element={
                    <TalentDetailPageGuard>
                      <TalentDetail />
                    </TalentDetailPageGuard>
                  } />
                  <Route path="/talents/:id/edit" element={
                    <TalentEditPageGuard>
                      <TalentEdit />
                    </TalentEditPageGuard>
                  } />

                  {/* Other pages with permission checks */}
                  <Route path="/candidates" element={
                    <CandidatesPageGuard>
                      <Candidates />
                    </CandidatesPageGuard>
                  } />
                  <Route path="/candidates/rejected" element={
                    <CandidatesPageGuard>
                      <RejectedCandidates />
                    </CandidatesPageGuard>
                  } />
                  {/* Одна страница на все стадии воронки кандидатов (video,
                      interview, pre_onboarding, onboarding...) — какие стадии
                      существуют, решает GET /stages?talent_type=candidate, а
                      не список роутов здесь. Должна идти после /candidates/rejected:
                      React Router v6 ранжирует статичные пути выше динамических
                      независимо от порядка объявления, но порядок важен для читаемости. */}
                  <Route path="/candidates/:stageKey" element={
                    <CandidatesPageGuard>
                      <CandidatesByStage />
                    </CandidatesPageGuard>
                  } />
                  <Route path="/employees" element={
                    <EmployeesPageGuard>
                      <Employees />
                    </EmployeesPageGuard>
                  } />
                  <Route path="/employees-all" element={
                    <EmployeesAllPageGuard>
                      <EmployeesAll />
                    </EmployeesAllPageGuard>
                  } />
                  <Route path="/employees-active" element={
                    <EmployeesPageGuard>
                      <EmployeesActive />
                    </EmployeesPageGuard>
                  } />
                  <Route path="/employees-project" element={
                    <EmployeesPageGuard>
                      <EmployeesProject />
                    </EmployeesPageGuard>
                  } />
                  <Route path="/presale" element={
                    <PreSalesPageGuard>
                      <PreSales />
                    </PreSalesPageGuard>
                  } />
                  <Route path="/presale/history" element={
                    <PreSalesPageGuard>
                      <PresaleHistory />
                    </PreSalesPageGuard>
                  } />
                  <Route path="/trainees" element={
                    <TraineesPageGuard>
                      <Trainees />
                    </TraineesPageGuard>
                  } />
                  <Route path="/statuses" element={
                    <StatusManagementPageGuard>
                      <StatusManagement />
                    </StatusManagementPageGuard>
                  } />
                  <Route path="/statuses/order-management" element={
                    <StatusManagementPageGuard>
                      <OrderManagement />
                    </StatusManagementPageGuard>
                  } />
                  <Route path="/stages" element={
                    <StageManagementPageGuard>
                      <StageManagement />
                    </StageManagementPageGuard>
                  } />

                  {/* Job Applications routes */}
                  <Route path="/job-applications" element={<JobApplications />} />
                  <Route path="/job-applications/new" element={<JobApplicationForm />} />
                  <Route path="/job-applications/:id" element={<JobApplicationDetail />} />
                  <Route path="/job-applications/:id/edit" element={<JobApplicationForm />} />

                  {/* Talent Posting routes */}
                  <Route path="/talent-templates" element={<TalentTemplates />} />
                  <Route path="/talent-templates/:id" element={<TalentTemplateDetail />} />
                  <Route path="/talent-posts" element={<TalentPosts />} />
                  <Route path="/talent-posts/:id" element={<TalentPostDetail />} />
                  <Route path="/prompts" element={<PromptTemplates />} />
                  <Route path="/prompts/:id" element={<PromptTemplateDetail />} />
                  <Route path="/notification-settings" element={<NotificationSettings />} />
                  <Route path="/playbook-settings" element={<PlaybookSettings />} />
                  <Route path="/stages-dashboard" element={<StagesDashboard />} />
                </Route>

                {/* 404 page */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Layout>
            </FavoritesProvider>
          </SidebarNavProvider>
        } />
        </Routes>
      </ErrorBoundary>
    </>
  )
}

export default App